// LayerContext.tsx
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Map } from 'ol';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { AddLayer, AddVectorTile, LayerGroup, LayerItem } from './types';
import { areLabelsActive } from '../styles/areLabelsActive';
import { getFeatureStyle, StyleFunction } from '../styles/getStyle';
import { HoverState, useVectorTiles } from '../vectorTiles/useVectorTiles';
import { useMapLayerHandler } from './useMapLayerHandler';
import { useMaxZoomOnLayers } from './useMaxZoomOnLayers';
import { getLayerTypeWithShips } from '../getLayerType';
import { baseColours } from '../styles/baseColours';
import { restyleTrackLayer } from '../tranformations/trackPathToVectorLayer';
import { useTimeZoneContext } from '@ais-ui/utils';
import Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import {
  addNewLayerAboveShipsLayer,
  addNewLayerAtTop,
  addNewLayerBelowShipsLayer,
  updateZIndex,
} from './addLayers';
import { calculateExtentWithAntimeridianSupport } from '../utils/calculateExtentWithAntimeridianSupport';
import { useOpenLayersContext } from '../OpenLayersContext';

export type RemoveLayerOnThresholdExceeded = (
  layer: LayerItem,
  onLoadFail: (layer: LayerItem) => void | undefined,
) => void;

interface LayerContextProps {
  layers: LayerItem[];
  addLayer: AddLayer;
  addLayerAtIndex: (
    layer: BaseLayer,
    name: string,
    index: number,
    isRemovableFromLayerControl?: boolean,
    canZoomToFromLayerControl?: boolean,
    canShowLabel?: boolean,
    properties?: Record<string, any>,
    id?: string,
    showLabel?: boolean,
    clickable?: boolean,
    hoverEnabled?: boolean,
  ) => LayerItem;
  addExistingLayerItem: (
    layer: BaseLayer,
    layerItem: LayerItem,
    position?: 'above-ships' | 'below-ships' | 'top',
  ) => void;
  addBaseLayer: (layer: BaseLayer, name: string, id?: string) => void;
  removeLayer: (layer: BaseLayer) => void;
  rearrangeLayer: (layer: LayerItem, newIndex: number) => void;
  getLayerById: (id: string) => LayerItem | undefined;
  doesLayerExist: (id: string) => boolean;
  zoomToLayer: (layer: VectorLayer<Feature<OlGeometry>>) => void;
  zoomToExtent: (extent: number[]) => void;
  toggleLayerVisibility: (layer: BaseLayer, isVisible: boolean) => void;
  removeLayerById: (id: string | undefined) => void;
  getBaseLayer: () => LayerItem | undefined;
  removeLayerOnThresholdExceeded: RemoveLayerOnThresholdExceeded;
  toggleLabels: (layer: LayerItem) => void;
  addVectorTile: AddVectorTile;
  removeLabels: (layer: LayerItem) => void;
  addLabels: (layer: LayerItem) => void;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  isCanvasLoaded: boolean;
  setIsCanvasLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  vectorTileHoverState: HoverState;
  changeColor: (layer: LayerItem, color: keyof typeof baseColours) => void;
  changeTransparency: (layer: LayerItem, transparency: number) => void;
  getLayersByIdContaining: (idSubstring: string) => LayerItem[];
  applyLayerOrdering: (
    orderedIds: string[],
    nameMapping?: Record<string, string>,
  ) => void;
  isShipLayerVisible: boolean;
  // Nested layer management (feature collections)
  addLayerWithChildren: (
    parent: Omit<LayerItem, 'layer' | 'errorCount' | 'successCount'>,
    children: LayerItem[],
    position?: 'above-ships' | 'below-ships' | 'top',
  ) => void;
  removeLayerWithChildren: (layerId: string) => void;
  patchLayer: (
    layerId: string,
    patch: { metadata?: string; properties?: Record<string, unknown> },
  ) => void;
  toggleChildrenVisibility: (layerId: string, isVisible: boolean) => void;
  toggleLayerExpanded: (layerId: string) => void;
  rearrangeChild: (
    parentId: string,
    child: LayerItem,
    newIndex: number,
  ) => void;
  /** @deprecated Use layers with children instead */
  groups: LayerGroup[];
  /** @deprecated Use addLayerWithChildren instead */
  addLayerGroup: (
    group: Omit<LayerGroup, 'isExpanded'>,
    children: LayerItem[],
  ) => void;
  /** @deprecated Use removeLayerWithChildren instead */
  removeLayerGroup: (groupId: string) => void;
  /** @deprecated Use toggleChildrenVisibility instead */
  toggleGroupVisibility: (groupId: string, isVisible: boolean) => void;
  /** @deprecated Use toggleLayerExpanded instead */
  toggleGroupExpanded: (groupId: string) => void;
  /** @deprecated Use getLayerById instead */
  getLayerGroup: (groupId: string) => LayerGroup | undefined;
}

const LayerContext = createContext<LayerContextProps | undefined>(undefined);

interface LayerProviderProps {
  map: Map;
  isDrawing: boolean;
  children: ReactNode;
}

export const initLayerItem = (
  layer: BaseLayer,
  name: string,
  isBase = false,
  isRemovableFromLayerControl = false,
  canZoomToFromLayerControl = false,
  canShowLabel = false,
  properties?: Record<string, any>,
  id?: string,
): LayerItem => {
  const usedId = id ? String(id) : uuidv4();

  layer.set('id', usedId);
  layer.set('name', name);
  layer.set('isBase', isBase);
  layer.set('isRemovableFromLayerControl', isRemovableFromLayerControl);
  layer.set('canZoomToFromLayerControl', canZoomToFromLayerControl);
  layer.set('canShowLabel', canShowLabel);
  layer.set('properties', properties ? properties : {});

  const errorCount = 0;
  const successCount = 0;

  const layerItemWithoutType: LayerItem = {
    id: usedId,
    name,
    isBase,
    isVisible: true,
    layer,
    isRemovableFromLayerControl,
    canZoomToFromLayerControl,
    canShowLabel,
    properties: properties ? properties : {},
    errorCount,
    successCount,
    type: 'unknown',
  };

  const layerType = getLayerTypeWithShips(layerItemWithoutType);

  return {
    ...layerItemWithoutType,
    type: layerType,
  };
};

const isLayerThresholdExceeded = (layer: LayerItem) => {
  const errorThreshold = 10;
  const successRatio =
    layer.successCount / (layer.successCount + layer.errorCount);

  return layer.errorCount > errorThreshold && successRatio < 0.2;
};

function updateLayerPropertiesAndStyle(
  layer: LayerItem,
  setLayers: React.Dispatch<React.SetStateAction<LayerItem[]>>,
  vectorLayer: VectorLayer<Feature<OlGeometry>>,
  showLabel: boolean,
  color: keyof typeof baseColours = 'blue',
  transparency: number,
  timezone?: string,
) {
  // Update the layer properties to trigger re-renders (search children too)
  setLayers((prevLayers) =>
    prevLayers.map((l) => {
      // Check children first (child layers live nested)
      if (l.children?.some((c) => c.id === layer.id)) {
        return {
          ...l,
          children: l.children.map((c) =>
            c.id === layer.id
              ? {
                  ...c,
                  properties: {
                    ...c.properties,
                    showLabel,
                    color,
                    transparency,
                  },
                }
              : c,
          ),
        };
      }
      return l.id === layer.id
        ? {
            ...l,
            properties: {
              ...l.properties,
              showLabel,
              color,
              transparency,
            },
          }
        : l;
    }),
  );

  if (
    layer.properties.type !== 'port' &&
    layer.properties.type !== 'source-identifier' &&
    layer.properties.type !== 'coverage' &&
    layer.properties.type !== 'gps-lost'
  ) {
    // Clear feature-level styles so the layer style takes precedence
    const source = vectorLayer.getSource();
    if (source instanceof VectorSource) {
      source.getFeatures().forEach((f) => f.setStyle(undefined));
    }

    vectorLayer.setStyle((feature) => {
      return getFeatureStyle(
        feature,
        color,
        'medium',
        'medium',
        showLabel,
        transparency,
        timezone,
      );
    });
  }
}

export const LayerProvider = ({
  map,
  isDrawing,
  children,
}: LayerProviderProps) => {
  const [layers, setLayers] = useState<LayerItem[]>([]);
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { timeZone } = useTimeZoneContext();

  // Derive groups from layers with children (backwards compat)
  const groups: LayerGroup[] = useMemo(
    () =>
      layers
        .filter((l) => l.children && l.children.length > 0)
        .map((l) => ({
          id: l.id,
          name: l.name,
          metadata: l.metadata,
          isExpanded: l.isExpanded ?? false,
        })),
    [layers],
  );

  const vectorTileLayers = useMemo(
    () =>
      layers.filter((layer) => {
        return layer.type === 'vectortile';
      }),
    [layers],
  );

  // canvasRef is used to render ship image
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isCanvasLoaded, setIsCanvasLoaded] = useState(false);

  const toggleLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const styleFunction = vectorLayer.getStyleFunction();
      const labelsAreActive = areLabelsActive(
        styleFunction as StyleFunction,
        source,
      );

      const color = layer.properties?.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        !labelsAreActive,
        color,
        transparency,
        timeZone,
      );
    },
    [timeZone],
  );

  const removeLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const color = layer.properties.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        false,
        color,
        transparency,
        timeZone,
      );
    },
    [timeZone],
  );

  const addLabels = useCallback(
    (layer: LayerItem) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const color = layer.properties?.color || 'blue';
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        true,
        color,
        transparency,
        timeZone,
      );
    },
    [timeZone],
  );

  const changeColor = useCallback(
    (layer: LayerItem, color: keyof typeof baseColours) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      // Vector tracks use feature-level styles — restyle via track helper
      if (
        layer.properties?.type === 'vector-track' &&
        layer.properties?.track
      ) {
        restyleTrackLayer(vectorLayer, layer.properties.track, {
          color: baseColours[color],
        });
        updateLayerPropertiesAndStyle(
          layer,
          setLayers,
          vectorLayer,
          false,
          color,
          0,
          timeZone,
        );
        return;
      }

      const showLabel = layer.properties?.showLabel || false;
      const transparency = layer.properties?.transparency || 0;

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        showLabel,
        color,
        transparency,
        timeZone,
      );
    },
    [timeZone],
  );

  const changeTransparency = useCallback(
    (layer: LayerItem, transparency: number) => {
      const vectorLayer = layer.layer as VectorLayer<Feature<OlGeometry>>;
      const source = vectorLayer.getSource();
      if (!(source instanceof VectorSource)) {
        return;
      }

      const showLabel = layer.properties.showLabel || false;
      const color = layer.properties.color || 'blue';

      updateLayerPropertiesAndStyle(
        layer,
        setLayers,
        vectorLayer,
        showLabel,
        color,
        transparency,
        timeZone,
      );
    },
    [timeZone],
  );

  // Internal helper function for common layer creation logic
  const createLayerItem = useCallback(
    (
      layer: BaseLayer,
      name: string,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      contextMenuEnabled?: boolean,
    ): LayerItem => {
      // We cannot use properties for showLabel, as only the owner of the shape can change these. So instead
      //  we use the showLabel parameter to populate the properties object, when the shape is added to the map.
      if (properties) {
        properties.showLabel = !!showLabel;
        properties.clickable = !!clickable;
        properties.hoverEnabled = !!hoverEnabled;
        properties.contextMenuEnabled = !!contextMenuEnabled;
      }

      const newLayerItem = initLayerItem(
        layer,
        name,
        false,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id,
      );

      map.addLayer(layer);

      if (canShowLabel && showLabel) {
        addLabels(newLayerItem);
      }

      return newLayerItem;
    },
    [addLabels, map],
  );

  // Internal helper function for positioning layer based on string position
  const addLayerWithStringPosition = useCallback(
    (
      newLayerItem: LayerItem,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships',
    ) => {
      setLayers((prevLayers) => {
        if (position === 'above-ships') {
          return addNewLayerAboveShipsLayer(prevLayers, newLayerItem);
        } else if (position === 'top') {
          return addNewLayerAtTop(prevLayers, newLayerItem);
        } else {
          return addNewLayerBelowShipsLayer(prevLayers, newLayerItem);
        }
      });
    },
    [],
  );

  // Internal helper function for positioning layer at specific index
  const addLayerWithNumericPosition = useCallback(
    (newLayerItem: LayerItem, index: number) => {
      setLayers((prevLayers) => {
        const updatedLayers = [...prevLayers];
        // Insert the layer at the specific index
        updatedLayers.splice(index, 0, newLayerItem);

        // Update Z-Index
        updateZIndex(updatedLayers);

        return updatedLayers;
      });
    },
    [],
  );

  const addLayer = useCallback(
    (
      layer: BaseLayer,
      name: string,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships',
      contextMenuEnabled?: boolean,
    ): LayerItem => {
      const newLayerItem = createLayerItem(
        layer,
        name,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id,
        showLabel,
        clickable,
        hoverEnabled,
        contextMenuEnabled,
      );

      addLayerWithStringPosition(newLayerItem, position);

      return newLayerItem;
    },
    [createLayerItem, addLayerWithStringPosition],
  );

  const addLayerAtIndex = useCallback(
    (
      layer: BaseLayer,
      name: string,
      index: number,
      isRemovableFromLayerControl = false,
      canZoomToFromLayerControl = false,
      canShowLabel = false,
      properties?: Record<string, any>,
      id?: string,
      showLabel?: boolean,
      clickable?: boolean,
      hoverEnabled?: boolean,
      contextMenuEnabled?: boolean,
    ): LayerItem => {
      const newLayerItem = createLayerItem(
        layer,
        name,
        isRemovableFromLayerControl,
        canZoomToFromLayerControl,
        canShowLabel,
        properties,
        id,
        showLabel,
        clickable,
        hoverEnabled,
        contextMenuEnabled,
      );

      addLayerWithNumericPosition(newLayerItem, index);

      return newLayerItem;
    },
    [createLayerItem, addLayerWithNumericPosition],
  );

  useMaxZoomOnLayers(map, layers);

  const { addVectorTile, vectorTileHoverState, onVectorTileHover } =
    useVectorTiles(addLayer);

  // This function is created so that we can start with a layerItem that has an empty layer, and thus track
  //  tile loading successes and errors from the start. If possible, use addLayer instead.
  const addExistingLayerItem = useCallback(
    (
      layer: BaseLayer,
      layerItem: LayerItem,
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships',
    ) => {
      layerItem.layer = layer;
      setLayers((prevLayers) => {
        if (position === 'above-ships') {
          return addNewLayerAboveShipsLayer(prevLayers, layerItem);
        } else if (position === 'top') {
          return addNewLayerAtTop(prevLayers, layerItem);
        } else {
          return addNewLayerBelowShipsLayer(prevLayers, layerItem);
        }
      });

      map.addLayer(layer);
    },
    [map],
  );

  const removeLayer = useCallback(
    (layer: BaseLayer) => {
      map.removeLayer(layer);
      setLayers((prevLayers) =>
        prevLayers
          .filter((l) => l.layer !== layer)
          .map((l) => {
            // Also remove from children if it's a nested layer
            if (l.children) {
              const filteredChildren = l.children.filter(
                (c) => c.layer !== layer,
              );
              if (filteredChildren.length !== l.children.length) {
                return { ...l, children: filteredChildren };
              }
            }
            return l;
          }),
      );
    },
    [map],
  );

  const addBaseLayer = useCallback(
    (layer: BaseLayer, name: string, id?: string) => {
      const newLayerItem = initLayerItem(
        layer,
        name,
        true,
        false,
        false,
        false,
        undefined,
        id,
      );

      setLayers((prevLayers) => {
        const baseLayers = prevLayers.filter((l) => l.isBase);
        baseLayers.forEach((baseLayerItem) => {
          removeLayer(baseLayerItem.layer);
        });

        const updatedLayers = [
          newLayerItem,
          ...prevLayers.filter((l) => !l.isBase),
        ];

        updateZIndex(updatedLayers);
        return updatedLayers;
      });

      // Add the new base layer to the map
      map.addLayer(layer);
    },
    [map, removeLayer],
  );

  const rearrangeLayer = useCallback(
    (layerItem: LayerItem, newIndex: number) => {
      setLayers((prevLayers) => {
        // Remove the item from its current position
        const updatedLayers = prevLayers.filter(
          (item) => item.id !== layerItem.id,
        );

        // Protect basemap: non-base layers must stay above base layers
        const baseLayerCount = updatedLayers.filter((l) => l.isBase).length;
        const safeIndex = layerItem.isBase
          ? newIndex
          : Math.max(newIndex, baseLayerCount);

        // Insert the item at the safe position
        updatedLayers.splice(safeIndex, 0, layerItem);

        // Update Z-Index
        updateZIndex(updatedLayers);

        // Return the updated array of layers
        return updatedLayers;
      });
    },
    [],
  );

  const applyLayerOrdering = useCallback(
    (orderedIds: string[], nameMapping?: Record<string, string>) => {
      setLayers((prevLayers) => {
        // Separate base layers — they must always stay at the bottom
        const baseLayers = prevLayers.filter((l) => l.isBase);
        const nonBaseLayers = prevLayers.filter((l) => !l.isBase);

        // Build ID-to-index map from the ordered IDs
        const idToIndex: Record<string, number> = {};
        orderedIds.forEach((id, index) => {
          idToIndex[id] = index;
        });

        // Resolve name-based mappings: for layers with unstable IDs (e.g. Vessels
        // gets a new UUID each session), map the stored ID to the actual layer ID
        if (nameMapping) {
          for (const [storedId, name] of Object.entries(nameMapping)) {
            if (idToIndex[storedId] !== undefined) {
              const actualLayer = nonBaseLayers.find(
                (l) => l.name.toLowerCase() === name.toLowerCase(),
              );
              if (actualLayer && actualLayer.id !== storedId) {
                idToIndex[actualLayer.id] = idToIndex[storedId];
              }
            }
          }
        }

        const sortedNonBase = [...nonBaseLayers].sort((a, b) => {
          const indexA = idToIndex[a.id] ?? Infinity;
          const indexB = idToIndex[b.id] ?? Infinity;
          return indexA - indexB;
        });

        const sorted = [...baseLayers, ...sortedNonBase];
        updateZIndex(sorted);
        return sorted;
      });
    },
    [],
  );

  const zoomToLayer = useCallback(
    (layer: VectorLayer<Feature<OlGeometry>>) => {
      const source = layer.getSource();

      if (!(source instanceof VectorSource)) {
        return;
      }

      // Use the helper function to calculate extent with antimeridian support, see case
      //  CLASSIC-8299 from MXAK
      const extent = calculateExtentWithAntimeridianSupport(
        source,
        displayProjection,
        dataProjection,
      );

      if (!extent) {
        return;
      }

      const isExtentValid = extent.every(
        (value) => value !== null && isFinite(value),
      );
      if (!isExtentValid) {
        return;
      }

      // Apply a buffer if there's only a single feature (like a point), to avoid excessive zooming
      if (source.getFeatures().length === 1) {
        const extentBuffer = 100;
        map.getView().fit(extent, {
          padding: [extentBuffer, extentBuffer, extentBuffer, extentBuffer],
          maxZoom: 16,
        });
      } else {
        map.getView().fit(extent, { maxZoom: 16 });
      }
    },
    [map, displayProjection, dataProjection],
  );

  const zoomToExtent = useCallback(
    (extent: number[]) => {
      if (!extent || !Array.isArray(extent) || extent.length !== 4) {
        console.warn('Provided extent is invalid:', extent);
        return;
      }

      map.getView().fit(extent);
    },
    [map],
  );

  const getLayerById = useCallback(
    (id: string): LayerItem | undefined => {
      for (const layerItem of layers) {
        if (layerItem.id === id) return layerItem;
        if (layerItem.children) {
          const child = layerItem.children.find((c) => c.id === id);
          if (child) return child;
        }
      }
      return undefined;
    },
    [layers],
  );

  const getLayersByIdContaining = useCallback(
    (idSubstring: string): LayerItem[] => {
      const results: LayerItem[] = [];
      for (const layerItem of layers) {
        if (layerItem.id?.includes(idSubstring)) results.push(layerItem);
        if (layerItem.children) {
          for (const child of layerItem.children) {
            if (child.id?.includes(idSubstring)) results.push(child);
          }
        }
      }
      return results;
    },
    [layers],
  );

  const getBaseLayer = useCallback(() => {
    return layers.find((layerItem) => layerItem.isBase);
  }, [layers]);

  const doesLayerExist = useCallback(
    (id: string): boolean => {
      return !!getLayerById(id);
    },
    [getLayerById],
  );

  const removeLayerById = useCallback(
    (id: string | undefined) => {
      if (id) {
        const existingLayer = getLayerById(id);
        if (existingLayer) {
          removeLayer(existingLayer.layer);
        }
      }
    },
    [getLayerById, removeLayer],
  );

  const toggleLayerVisibility = useCallback(
    (layer: BaseLayer, isVisible: boolean) => {
      layer.setVisible(isVisible);
      setLayers((prevLayers) =>
        prevLayers.map((l) => {
          // Check children first — parent.layer may reference first child's layer
          if (l.children?.some((c) => c.layer === layer)) {
            return {
              ...l,
              children: l.children.map((c) =>
                c.layer === layer ? { ...c, isVisible } : c,
              ),
            };
          }
          if (l.layer === layer) return { ...l, isVisible };
          return l;
        }),
      );
    },
    [],
  );

  const removeLayerOnThresholdExceeded = useCallback(
    (
      layer: LayerItem,
      onLoadFail: ((layer: LayerItem) => void) | undefined,
    ) => {
      if (isLayerThresholdExceeded(layer)) {
        removeLayer(layer.layer);
        if (onLoadFail) {
          onLoadFail(layer);
        }
      }
    },
    [removeLayer],
  );

  const isShipLayerVisible = useMemo(() => {
    const shipLayer = layers.find((layer) => layer.name === 'Vessels') || null;

    if (!shipLayer) {
      return false;
    }

    return shipLayer.isVisible;
  }, [layers]);

  useMapLayerHandler(
    map,
    isDrawing,
    canvasRef,
    vectorTileLayers,
    onVectorTileHover,
    dataProjection,
  );

  // Clear vector tile hover when the mouse leaves the map or layers are removed
  useEffect(() => {
    const clearHover = () => {
      onVectorTileHover({ feature: null, position: null });
    };

    const mapElement = map.getTargetElement();
    mapElement?.addEventListener('pointerleave', clearHover);

    const layersCollection = map.getLayers();
    layersCollection.on('remove', clearHover);

    return () => {
      mapElement?.removeEventListener('pointerleave', clearHover);
      layersCollection.un('remove', clearHover);
    };
  }, [map, onVectorTileHover]);

  // --- Nested Layer Management (Feature Collections) ---

  const addLayerWithChildren = useCallback(
    (
      parent: Omit<LayerItem, 'layer' | 'errorCount' | 'successCount'>,
      childLayerItems: LayerItem[],
      position: 'above-ships' | 'below-ships' | 'top' = 'below-ships',
    ) => {
      // The parent is a virtual layer (no OL layer of its own) that groups children
      const parentItem: LayerItem = {
        ...parent,
        layer: childLayerItems[0]?.layer, // Use first child's layer as reference for type detection
        errorCount: 0,
        successCount: 0,
        isExpanded: false,
        children: childLayerItems,
        isVisible: true,
      };

      setLayers((prev) => {
        if (position === 'above-ships') {
          return addNewLayerAboveShipsLayer(prev, parentItem);
        } else if (position === 'top') {
          return addNewLayerAtTop(prev, parentItem);
        }
        return addNewLayerBelowShipsLayer(prev, parentItem);
      });

      // Add each child's OL layer to the map
      for (const child of childLayerItems) {
        map.addLayer(child.layer);
      }
    },
    [map],
  );

  const removeLayerWithChildren = useCallback(
    (layerId: string) => {
      const parentLayer = layers.find((l) => l.id === layerId);
      if (parentLayer?.children) {
        for (const child of parentLayer.children) {
          map.removeLayer(child.layer);
        }
      }
      setLayers((prev) => prev.filter((l) => l.id !== layerId));
    },
    [map, layers],
  );

  /** Update metadata/properties on a layer (or child) without removing it. */
  const patchLayer = useCallback(
    (
      layerId: string,
      patch: { metadata?: string; properties?: Record<string, unknown> },
    ) => {
      setLayers((prev) =>
        prev.map((l) => {
          // Direct match (top-level or parent)
          if (l.id === layerId) {
            return {
              ...l,
              ...(patch.metadata !== undefined && { metadata: patch.metadata }),
              ...(patch.properties && {
                properties: { ...l.properties, ...patch.properties },
              }),
            };
          }
          // Check children
          if (l.children?.some((c) => c.id === layerId)) {
            return {
              ...l,
              children: l.children.map((c) =>
                c.id === layerId
                  ? {
                      ...c,
                      ...(patch.metadata !== undefined && {
                        metadata: patch.metadata,
                      }),
                      ...(patch.properties && {
                        properties: { ...c.properties, ...patch.properties },
                      }),
                    }
                  : c,
              ),
            };
          }
          return l;
        }),
      );
    },
    [],
  );

  const toggleChildrenVisibility = useCallback(
    (layerId: string, isVisible: boolean) => {
      setLayers((prev) =>
        prev.map((l) => {
          if (l.id === layerId && l.children) {
            const updatedChildren = l.children.map((child) => {
              child.layer.setVisible(isVisible);
              return { ...child, isVisible };
            });
            return { ...l, isVisible, children: updatedChildren };
          }
          return l;
        }),
      );
    },
    [],
  );

  const toggleLayerExpanded = useCallback((layerId: string) => {
    setLayers((prev) =>
      prev.map((l) =>
        l.id === layerId ? { ...l, isExpanded: !l.isExpanded } : l,
      ),
    );
  }, []);

  const rearrangeChild = useCallback(
    (parentId: string, child: LayerItem, newIndex: number) => {
      setLayers((prev) =>
        prev.map((l) => {
          if (l.id === parentId && l.children) {
            const updatedChildren = l.children.filter((c) => c.id !== child.id);
            const safeIndex = Math.max(
              0,
              Math.min(newIndex, updatedChildren.length),
            );
            updatedChildren.splice(safeIndex, 0, child);
            return { ...l, children: updatedChildren };
          }
          return l;
        }),
      );
    },
    [],
  );

  // --- Deprecated group methods (delegate to new nested layer API) ---

  const addLayerGroup = useCallback(
    (group: Omit<LayerGroup, 'isExpanded'>, childLayerItems: LayerItem[]) => {
      addLayerWithChildren(
        {
          id: group.id,
          name: group.name,
          metadata: group.metadata,
          isBase: false,
          isVisible: true,
          isRemovableFromLayerControl: true,
          canZoomToFromLayerControl: true,
          canShowLabel: false,
          properties: { type: 'featurecollection' },
          type: 'featurecollection',
        },
        childLayerItems,
      );
    },
    [addLayerWithChildren],
  );

  const removeLayerGroup = useCallback(
    (groupId: string) => {
      removeLayerWithChildren(groupId);
    },
    [removeLayerWithChildren],
  );

  const toggleGroupVisibility = useCallback(
    (groupId: string, isVisible: boolean) => {
      toggleChildrenVisibility(groupId, isVisible);
    },
    [toggleChildrenVisibility],
  );

  const toggleGroupExpanded = useCallback(
    (groupId: string) => {
      toggleLayerExpanded(groupId);
    },
    [toggleLayerExpanded],
  );

  const getLayerGroup = useCallback(
    (groupId: string): LayerGroup | undefined => {
      const layer = layers.find((l) => l.id === groupId);
      if (!layer?.children) return undefined;
      return {
        id: layer.id,
        name: layer.name,
        metadata: layer.metadata,
        isExpanded: layer.isExpanded ?? false,
      };
    },
    [layers],
  );

  const value = {
    layers,
    addLayer,
    addLayerAtIndex,
    addExistingLayerItem,
    addBaseLayer,
    removeLayer,
    rearrangeLayer,
    applyLayerOrdering,
    getLayerById,
    doesLayerExist,
    zoomToLayer,
    zoomToExtent,
    toggleLayerVisibility,
    removeLayerById,
    getBaseLayer,
    removeLayerOnThresholdExceeded,
    toggleLabels,
    addVectorTile,
    vectorTileHoverState,
    removeLabels,
    addLabels,
    canvasRef,
    isCanvasLoaded,
    setIsCanvasLoaded,
    changeColor,
    changeTransparency,
    getLayersByIdContaining,
    isShipLayerVisible,
    // New nested layer API
    addLayerWithChildren,
    removeLayerWithChildren,
    patchLayer,
    toggleChildrenVisibility,
    toggleLayerExpanded,
    rearrangeChild,
    // Deprecated group API (delegates to new API)
    groups,
    addLayerGroup,
    removeLayerGroup,
    toggleGroupVisibility,
    toggleGroupExpanded,
    getLayerGroup,
  };

  return (
    <LayerContext.Provider value={value}>{children}</LayerContext.Provider>
  );
};

export const useLayers = (): LayerContextProps => {
  const context = useContext(LayerContext);
  if (!context) {
    throw new Error('useLayers must be used within a LayerProvider');
  }
  return context;
};
